import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Eye, EyeOff, Copy, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BlockRenderer, { BLOCK_COMPONENTS, blockStyle } from '@/components/BlockRenderer';
import { BLOCK_TYPES } from './BlockPalette';

// ====== Sortable Block Wrapper ======

function SortableBlock({ block, isSelected, onSelect, onDelete, onDuplicate, onMoveUp, onMoveDown, onToggleVisibility, isFirst, isLast, subdomain, primaryColor }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto'
  };

  const Component = BLOCK_COMPONENTS[block.type];
  const blockInfo = BLOCK_TYPES.find(bt => bt.type === block.type);
  const hidden = block.is_visible === false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-lg transition-all ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : 'ring-1 ring-transparent hover:ring-slate-300'} ${hidden ? 'opacity-40' : ''}`}
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
    >
      {/* Toolbar overlay */}
      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 bg-white border rounded-full shadow-md px-1 py-0.5 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <div {...attributes} {...listeners} className="cursor-grab p-1 hover:bg-slate-100 rounded-full" title="Seret untuk pindahkan">
          <GripVertical size={14} className="text-slate-400" />
        </div>
        <span className="text-[10px] font-semibold text-slate-500 px-1 select-none uppercase">{block.type}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst} title="Pindah ke atas">
          <ChevronUp size={12} />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast} title="Pindah ke bawah">
          <ChevronDown size={12} />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={(e) => { e.stopPropagation(); onToggleVisibility(block.id, !hidden); }} title={hidden ? 'Tampilkan' : 'Sembunyikan'}>
          {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplikat">
          <Copy size={12} />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full hover:bg-red-50 hover:text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Hapus">
          <Trash2 size={12} />
        </Button>
      </div>

      {/* Block preview */}
      <div className="pointer-events-none">
        {Component ? (
          <Component content={block.content || {}} style={block.style || {}} primaryColor={primaryColor} subdomain={subdomain} />
        ) : (
          <div className="p-8 text-center text-slate-400 bg-slate-50 rounded-lg">
            Block type: {block.type}
          </div>
        )}
      </div>
    </div>
  );
}

// ====== Main EditorCanvas ======

export default function EditorCanvas({
  blocks, setBlocks, selectedBlockId, onSelectBlock,
  onDeleteBlock, onDuplicateBlock, onToggleVisibility,
  subdomain, primaryColor, deviceWidth
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex(b => b.id === active.id);
    const newIndex = blocks.findIndex(b => b.id === over.id);
    const newBlocks = arrayMove(blocks, oldIndex, newIndex).map((b, i) => ({ ...b, order: i }));
    setBlocks(newBlocks);
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const newBlocks = arrayMove(blocks, index, index - 1).map((b, i) => ({ ...b, order: i }));
    setBlocks(newBlocks);
  };

  const handleMoveDown = (index) => {
    if (index >= blocks.length - 1) return;
    const newBlocks = arrayMove(blocks, index, index + 1).map((b, i) => ({ ...b, order: i }));
    setBlocks(newBlocks);
  };

  const canvasStyle = {};
  if (deviceWidth === 'tablet') canvasStyle.maxWidth = '768px';
  else if (deviceWidth === 'mobile') canvasStyle.maxWidth = '375px';

  return (
    <div
      className="flex-1 overflow-y-auto bg-slate-100 p-6"
      onClick={() => onSelectBlock(null)}
    >
      <div
        className="mx-auto bg-white rounded-xl shadow-sm min-h-[600px] transition-all duration-300"
        style={{ ...canvasStyle, maxWidth: canvasStyle.maxWidth || '1200px' }}
      >
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <div className="text-5xl mb-4">🧱</div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Canvas Kosong</h3>
            <p className="text-sm text-slate-500 max-w-xs">
              Klik block di panel kiri untuk menambahkan section ke halaman Anda
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4 p-4">
                {blocks.map((block, index) => (
                  <SortableBlock
                    key={block.id}
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onSelect={onSelectBlock}
                    onDelete={() => onDeleteBlock(block.id)}
                    onDuplicate={() => onDuplicateBlock(block.id)}
                    onMoveUp={() => handleMoveUp(index)}
                    onMoveDown={() => handleMoveDown(index)}
                    onToggleVisibility={onToggleVisibility}
                    isFirst={index === 0}
                    isLast={index === blocks.length - 1}
                    subdomain={subdomain}
                    primaryColor={primaryColor}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
